import { useState, useEffect, useRef } from 'react';
import { Link, NavLink } from 'react-router-dom';
import Logo from '../../../assetes/logos.png';

// ============================================================
// 1. COMPOSANT PARTICULES (fond animé)
// ============================================================
const ParticlesBackground = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let animationId;
    let particles = [];

    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    window.addEventListener('resize', resizeCanvas);
    resizeCanvas();

    class Particle {
      constructor() {
        this.x = Math.random() * canvas.width;
        this.y = Math.random() * canvas.height;
        this.size = Math.random() * 2 + 0.5;
        this.speedX = (Math.random() - 0.5) * 0.4;
        this.speedY = (Math.random() - 0.5) * 0.4;
        this.opacity = Math.random() * 0.5 + 0.15;
      }
      update() {
        this.x += this.speedX;
        this.y += this.speedY;
        if (this.x < 0 || this.x > canvas.width) this.speedX *= -1;
        if (this.y < 0 || this.y > canvas.height) this.speedY *= -1;
      }
      draw() {
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(180, 210, 255, ${this.opacity})`;
        ctx.fill();
      }
    }

    const initParticles = () => {
      particles = [];
      const count = Math.min(60, Math.floor((canvas.width * canvas.height) / 20000));
      for (let i = 0; i < count; i++) {
        particles.push(new Particle());
      }
    };
    initParticles();

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particles.forEach(p => {
        p.update();
        p.draw();
      });
      // Lignes entre particules proches
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 120) {
            ctx.beginPath();
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.strokeStyle = `rgba(180, 210, 255, ${0.05 * (1 - dist / 120)})`;
            ctx.lineWidth = 0.6;
            ctx.stroke();
          }
        }
      }
      animationId = requestAnimationFrame(animate);
    };
    animate();

    return () => {
      cancelAnimationFrame(animationId);
      window.removeEventListener('resize', resizeCanvas);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 0,
        opacity: 0.5,
      }}
    />
  );
};

// ============================================================
// 2. COMPOSANT PRINCIPAL NAVBAR
// ============================================================
function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  // Gestion du scroll pour ajouter la classe 'scrolled'
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Fermeture du menu mobile quand on clique sur un lien
  const closeMobile = () => setMobileOpen(false);

  return (
    <>
      {/* Fond de particules en arrière‑plan fixe */}
      <ParticlesBackground />

      <style>{`
        /* ============================================
           ROOT VARIABLES
           ============================================ */
        :root {
          --blue-primary: #0a1628;
          --blue-secondary: #0f2140;
          --blue-accent: #1a3a6b;
          --blue-glow: #2a5fa8;
          --blue-light: #4a8ad4;
          --white: #ffffff;
          --white-muted: rgba(255,255,255,0.7);
          --shadow-heavy: 0 8px 32px rgba(0,0,0,0.5);
          --shadow-soft: 0 4px 20px rgba(0,0,0,0.25);
          --transition-smooth: all 0.35s cubic-bezier(0.25, 0.46, 0.45, 0.94);
          --transition-bounce: all 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
        }

        /* ============================================
           HEADER / NAVBAR
           ============================================ */
        .ve-header {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          z-index: 9999;
          background: rgba(10, 22, 40, 0.85);
          backdrop-filter: blur(12px);
          -webkit-backdrop-filter: blur(12px);
          border-bottom: 1px solid rgba(255,255,255,0.06);
          box-shadow: var(--shadow-heavy);
          transition: var(--transition-smooth);
        }

        .ve-header.scrolled {
          background: rgba(10, 22, 40, 0.92);
          box-shadow: 0 4px 30px rgba(0,0,0,0.6);
          border-bottom-color: rgba(255,255,255,0.10);
        }

        .container-fluid {
          max-width: 1320px;
          margin: 0 auto;
          padding: 0 24px;
        }

        .ve-nav-wrap {
          display: flex;
          align-items: center;
          justify-content: space-between;
          height: 76px;
          position: relative;
          z-index: 2;
        }

        /* ============================================
           LOGO – SUPER TITRE ANIMÉ
           ============================================ */
        .ve-logo a {
          display: flex;
          align-items: center;
          gap: 10px;
          text-decoration: none;
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          font-weight: 700;
          font-size: 1.5rem;
          letter-spacing: -0.5px;
          position: relative;
        }

        .ve-logo-icon {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 40px;
          height: 40px;
          background: linear-gradient(135deg, var(--blue-glow), var(--blue-light));
          border-radius: 12px;
          font-size: 1.3rem;
          font-weight: 800;
          color: var(--white);
          box-shadow: 0 0 20px rgba(42, 95, 168, 0.35);
          transition: var(--transition-bounce);
          animation: logoPulse 3s ease-in-out infinite;
        }

        @keyframes logoPulse {
          0%, 100% { box-shadow: 0 0 20px rgba(42, 95, 168, 0.35); transform: scale(1); }
          50% { box-shadow: 0 0 40px rgba(42, 95, 168, 0.55); transform: scale(1.04); }
        }

        .ve-logo-icon:hover {
          transform: scale(1.10) rotate(-4deg);
        }

        /* Texte du logo avec dégradé animé */
        .ve-logo-text {
          font-size: 1.35rem;
          font-weight: 600;
          color: var(--white);
          letter-spacing: -0.3px;
          background: linear-gradient(135deg, #f0f4ff 0%, #b0d4ff 50%, #f0f4ff 100%);
          background-size: 200% 200%;
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          animation: gradientText 4s ease-in-out infinite alternate;
          text-shadow: 0 0 30px rgba(107, 179, 232, 0.15);
        }

        @keyframes gradientText {
          0% { background-position: 0% 50%; }
          100% { background-position: 100% 50%; }
        }

        .ve-logo-text strong {
          font-weight: 700;
          position: relative;
          -webkit-text-fill-color: initial; /* pour que le strong reste visible */
          background: linear-gradient(135deg, var(--blue-light), #6bb3e8);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .ve-logo-text strong::after {
          content: '';
          position: absolute;
          bottom: -2px;
          left: 0;
          width: 100%;
          height: 2px;
          background: var(--blue-light);
          border-radius: 2px;
          transform: scaleX(0);
          transform-origin: right;
          transition: transform 0.4s ease;
        }

        .ve-logo a:hover .ve-logo-text strong::after {
          transform: scaleX(1);
          transform-origin: left;
        }

        /* ============================================
           NAVIGATION LINKS
           ============================================ */
        .ve-nav {
          display: flex;
          align-items: center;
        }

        .ve-nav ul {
          display: flex;
          align-items: center;
          list-style: none;
          margin: 0;
          padding: 0;
          gap: 4px;
        }

        .ve-nav ul li {
          position: relative;
          opacity: 0;
          animation: fadeInDown 0.5s ease forwards;
        }

        .ve-nav ul li:nth-child(1) { animation-delay: 0.05s; }
        .ve-nav ul li:nth-child(2) { animation-delay: 0.10s; }
        .ve-nav ul li:nth-child(3) { animation-delay: 0.15s; }
        .ve-nav ul li:nth-child(4) { animation-delay: 0.20s; }
        .ve-nav ul li:nth-child(5) { animation-delay: 0.25s; }
        .ve-nav ul li:nth-child(6) { animation-delay: 0.30s; }

        @keyframes fadeInDown {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .ve-nav ul li a {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          padding: 8px 18px;
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          font-weight: 500;
          font-size: 0.92rem;
          color: var(--white-muted);
          text-decoration: none;
          border-radius: 8px;
          transition: var(--transition-smooth);
          position: relative;
          letter-spacing: 0.2px;
        }

        .ve-nav ul li a::after {
          content: '';
          position: absolute;
          bottom: 2px;
          left: 50%;
          width: 0;
          height: 2.5px;
          background: linear-gradient(90deg, var(--blue-glow), var(--blue-light));
          border-radius: 4px;
          transition: var(--transition-smooth);
          transform: translateX(-50%);
        }

        .ve-nav ul li a:hover,
        .ve-nav ul li a.active {
          color: var(--white);
          background: rgba(255,255,255,0.06);
        }

        .ve-nav ul li a:hover::after,
        .ve-nav ul li a.active::after {
          width: 60%;
        }

        .ve-nav ul li a i {
          font-size: 0.7rem;
          margin-left: 2px;
          transition: transform 0.3s ease;
        }

        .ve-nav ul li.has-drop:hover > a i {
          transform: rotate(180deg);
        }

        /* ============================================
           DROPDOWN (exemple, si utilisé)
           ============================================ */
        .ve-dropdown {
          position: absolute;
          top: calc(100% + 12px);
          left: 50%;
          transform: translateX(-50%) translateY(10px);
          min-width: 210px;
          background: rgba(15, 33, 64, 0.95);
          backdrop-filter: blur(12px);
          border-radius: 14px;
          padding: 10px 6px;
          list-style: none;
          box-shadow: var(--shadow-heavy);
          border: 1px solid rgba(255,255,255,0.07);
          opacity: 0;
          visibility: hidden;
          pointer-events: none;
          transition: var(--transition-smooth);
        }

        .ve-dropdown::before {
          content: '';
          position: absolute;
          top: -8px;
          left: 50%;
          transform: translateX(-50%);
          border-left: 8px solid transparent;
          border-right: 8px solid transparent;
          border-bottom: 8px solid rgba(15, 33, 64, 0.95);
        }

        .ve-nav ul li.has-drop:hover .ve-dropdown {
          opacity: 1;
          visibility: visible;
          pointer-events: auto;
          transform: translateX(-50%) translateY(0);
        }

        .ve-dropdown li {
          margin: 0;
          padding: 0;
        }

        .ve-dropdown li a {
          display: block;
          padding: 10px 18px;
          font-size: 0.88rem;
          font-weight: 450;
          color: var(--white-muted);
          border-radius: 8px;
          background: transparent;
          transition: var(--transition-smooth);
          text-align: left;
          letter-spacing: 0.1px;
        }

        .ve-dropdown li a::after {
          display: none;
        }

        .ve-dropdown li a:hover {
          color: var(--white);
          background: rgba(74, 138, 212, 0.18);
          transform: translateX(4px);
        }

        /* ============================================
           CTA BUTTON
           ============================================ */
        .ve-nav-cta {
          display: flex;
          align-items: center;
        }

        .ve-cta-btn {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          padding: 10px 26px;
          background: linear-gradient(135deg, var(--blue-glow), var(--blue-light));
          color: var(--white) !important;
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          font-weight: 600;
          font-size: 0.9rem;
          border-radius: 50px;
          text-decoration: none;
          box-shadow: 0 0 24px rgba(42, 95, 168, 0.30);
          transition: var(--transition-bounce);
          border: none;
          cursor: pointer;
          position: relative;
          overflow: hidden;
        }

        .ve-cta-btn::before {
          content: '';
          position: absolute;
          inset: 0;
          background: linear-gradient(135deg, var(--blue-light), var(--blue-glow));
          opacity: 0;
          transition: opacity 0.4s ease;
          border-radius: 50px;
        }

        .ve-cta-btn:hover::before {
          opacity: 1;
        }

        .ve-cta-btn:hover {
          transform: translateY(-2px) scale(1.02);
          box-shadow: 0 0 40px rgba(42, 95, 168, 0.50);
        }

        .ve-cta-btn i {
          font-size: 0.85rem;
          transition: transform 0.4s ease;
          position: relative;
          z-index: 1;
        }

        .ve-cta-btn:hover i {
          transform: translateX(5px);
        }

        .ve-cta-btn span {
          position: relative;
          z-index: 1;
        }

        /* ============================================
           MOBILE TOGGLE
           ============================================ */
        .ve-toggler {
          display: none;
          flex-direction: column;
          gap: 5px;
          background: transparent;
          border: none;
          cursor: pointer;
          padding: 8px 4px;
          z-index: 100;
        }

        .ve-toggler span {
          display: block;
          width: 28px;
          height: 2.5px;
          background: var(--white);
          border-radius: 4px;
          transition: var(--transition-smooth);
          transform-origin: center;
        }

        .ve-toggler.open span:nth-child(1) {
          transform: translateY(7.5px) rotate(45deg);
        }

        .ve-toggler.open span:nth-child(2) {
          opacity: 0;
          transform: scaleX(0);
        }

        .ve-toggler.open span:nth-child(3) {
          transform: translateY(-7.5px) rotate(-45deg);
        }

        /* ============================================
           MOBILE MENU (avec overlay)
           ============================================ */
        .ve-mobile-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0,0,0,0.55);
          backdrop-filter: blur(6px);
          z-index: 9998;
          opacity: 0;
          visibility: hidden;
          transition: var(--transition-smooth);
        }

        .ve-mobile-overlay.open {
          opacity: 1;
          visibility: visible;
        }

        .ve-mobile-menu {
          position: fixed;
          top: 0;
          right: -100%;
          width: 100%;
          max-width: 380px;
          height: 100vh;
          background: rgba(10, 22, 40, 0.96);
          backdrop-filter: blur(20px);
          padding: 90px 32px 40px;
          box-shadow: var(--shadow-heavy);
          transition: right 0.5s cubic-bezier(0.22, 1, 0.36, 1);
          overflow-y: auto;
          border-left: 1px solid rgba(255,255,255,0.06);
          z-index: 9999;
        }

        .ve-mobile-menu.open {
          right: 0;
        }

        .ve-mobile-menu ul {
          list-style: none;
          padding: 0;
          margin: 0;
          display: flex;
          flex-direction: column;
          gap: 6px;
        }

        .ve-mobile-menu ul li a {
          display: block;
          padding: 14px 18px;
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          font-weight: 500;
          font-size: 1.15rem;
          color: var(--white-muted);
          text-decoration: none;
          border-radius: 12px;
          transition: var(--transition-smooth);
          border: 1px solid transparent;
        }

        .ve-mobile-menu ul li a:hover {
          color: var(--white);
          background: rgba(255,255,255,0.06);
          border-color: rgba(255,255,255,0.08);
          transform: translateX(6px);
        }

        /* ============================================
           RESPONSIVE
           ============================================ */
        @media (max-width: 1024px) {
          .ve-nav ul li a {
            padding: 8px 14px;
            font-size: 0.85rem;
          }
          .ve-cta-btn {
            padding: 8px 20px;
            font-size: 0.82rem;
          }
          .ve-logo-text {
            font-size: 1.15rem;
          }
        }

        @media (max-width: 860px) {
          .ve-nav {
            display: none;
          }
          .ve-nav-cta {
            display: none;
          }
          .ve-toggler {
            display: flex;
          }
          .ve-nav-wrap {
            height: 68px;
          }
          .ve-logo-icon {
            width: 36px;
            height: 36px;
            font-size: 1.1rem;
          }
          .ve-logo-text {
            font-size: 1.1rem;
          }
          .ve-mobile-menu ul li a {
            font-size: 1.05rem;
            padding: 14px 16px;
          }
        }

        @media (max-width: 480px) {
          .container-fluid {
            padding: 0 16px;
          }
          .ve-logo-text {
            font-size: 1rem;
          }
          .ve-logo-icon {
            width: 32px;
            height: 32px;
            font-size: 1rem;
          }
          .ve-mobile-menu {
            max-width: 100%;
            padding: 80px 20px 30px;
          }
        }

        /* ============================================
           FALLBACK ICONS (FontAwesome)
           ============================================ */
        .fa, .fas, .far, .fal, .fab {
          font-family: 'Font Awesome 6 Free' !important;
          font-weight: 900;
        }
        .fa-angle-down::before { content: "▾"; font-size: 0.7rem; }
        .fa-arrow-right::before { content: "→"; font-size: 0.85rem; }
      `}</style>

      <header className={`ve-header ${scrolled ? 'scrolled' : ''}`}>
        <div className="container-fluid ve-nav-wrap">
          {/* Logo – Super Titre */}
          <div className="ve-logo">
            <Link to="/">
              <img src={Logo} style={{ borderRadius: '20px', width: '130px' }} alt="Logo" />
            </Link>
          </div>

          {/* Navigation Desktop */}
          <nav className="ve-nav">
            <ul>
              <li>
                <NavLink to="/" end className={({ isActive }) => (isActive ? 'active' : '')}>
                  Home
                </NavLink>
              </li>
              <li>
                <NavLink to="/about">A propos</NavLink>
              </li>
              <li>
                <NavLink to="/services">Services</NavLink>
              </li>
              <li>
                <NavLink to="/nos-formations">Formations</NavLink>
              </li>
              <li>
                <NavLink to="/nos-offres">Offres d'emplois</NavLink>
              </li>
            </ul>
          </nav>

          {/* CTA Button */}
          <div className="ve-nav-cta">
            <Link to="/contact" className="ve-cta-btn">
              <span>Contacts</span> <i className="fa fa-arrow-right"></i>
            </Link>
          </div>

          {/* Mobile Toggle */}
          <button
            className={`ve-toggler ${mobileOpen ? 'open' : ''}`}
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Toggle menu"
          >
            <span></span>
            <span></span>
            <span></span>
          </button>
        </div>

        {/* Overlay pour fermer le menu en cliquant à l'extérieur */}
        <div
          className={`ve-mobile-overlay ${mobileOpen ? 'open' : ''}`}
          onClick={closeMobile}
        ></div>

        {/* Mobile Menu */}
        <div className={`ve-mobile-menu ${mobileOpen ? 'open' : ''}`}>
          <ul>
            <li><Link to="/" onClick={closeMobile}>Home</Link></li>
            <li><Link to="/about" onClick={closeMobile}>A propos</Link></li>
            <li><Link to="/services" onClick={closeMobile}>Services</Link></li>
            <li><Link to="/nos-formations" onClick={closeMobile}>Formations</Link></li>
            <li><Link to="/nos-offres" onClick={closeMobile}>Offres d'emplois</Link></li>
            <li><Link to="/contact" onClick={closeMobile}>Contact</Link></li>
          </ul>
        </div>
      </header>
    </>
  );
}

export default Navbar;